'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { apiClient } from '@/lib/api-client';
import { getAccessToken, isAuthSessionEnded } from '@/lib/auth-storage';
import type { UserSettings } from '@/lib/types';
import { primeAppLanguageCache } from '@/lib/i18n/language-storage';
import { isAuthExemptPublicRoute } from '@/lib/i18n/public-serbian-routes';

interface UserSettingsContextType {
  settings: UserSettings | null;
  loading: boolean;
  reloadSettings: () => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

function hasAuthToken(): boolean {
  // Require in-memory JWT — hasStoredUser alone races SessionRestore / Layout refresh.
  return typeof window !== 'undefined' && !!getAccessToken();
}

function isNetworkOrSessionError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('failed to fetch') ||
      msg.includes('network') ||
      msg.includes('session_expired') ||
      msg.includes('invalid_refresh_token') ||
      msg.includes('invalid token') ||
      msg.includes('neispravan token') ||
      msg.includes('unauthorized')
    );
  }
  return false;
}

export function UserSettingsProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  // Reload only when crossing the public/app boundary. A full pathname dep
  // refetches on every menu click and blanks the sidebar until features return.
  const authExempt = isAuthExemptPublicRoute(pathname);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (isAuthExemptPublicRoute(window.location.pathname)) return false;
    return hasAuthToken();
  });
  const loadInFlightRef = useRef(false);

  const loadSettings = useCallback(async () => {
    if (authExempt) {
      setSettings(null);
      setLoading(false);
      return;
    }

    if (!hasAuthToken()) {
      setSettings(null);
      setLoading(false);
      return;
    }

    if (loadInFlightRef.current) {
      return;
    }

    loadInFlightRef.current = true;
    try {
      setLoading(true);
      const data = await apiClient.getUserSettings();
      setSettings(data);
      primeAppLanguageCache(data?.language);
    } catch (error) {
      // api-client already refreshes / clears session on 401 — do not double-clear here.
      if (!isAuthSessionEnded() && !isNetworkOrSessionError(error)) {
        console.warn('Error loading user settings:', error);
      }
      setSettings(null);
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  }, [authExempt]);

  useEffect(() => {
    void loadSettings().catch(() => {
      setSettings(null);
      setLoading(false);
      loadInFlightRef.current = false;
    });

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_settings_updated') {
        void loadSettings();
      }
    };

    const handleCustomEvent = () => {
      void loadSettings();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userSettingsUpdated', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userSettingsUpdated', handleCustomEvent);
    };
  }, [loadSettings]);

  return (
    <UserSettingsContext.Provider value={{ settings, loading, reloadSettings: loadSettings }}>
      {children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
}

/** Notify other tabs/pages after the user explicitly saved settings (not after a plain GET). */
export function broadcastUserSettingsUpdate() {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('user_settings_updated', Date.now().toString());
    window.dispatchEvent(new Event('userSettingsUpdated'));
  }
}
